import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Clock, DollarSign, Bell, Home, Palette, Upload, Image } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DaycareSettings({ tenantId, onBrandingChange }) {
  const [settings, setSettings] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (!tenantId) return;
    const fetchAll = async () => {
      try {
        const [settingsRes, brandingRes] = await Promise.all([
          axios.get(`${API}/daycare/${tenantId}/settings`),
          axios.get(`${API}/branding/${tenantId}`),
        ]);
        setSettings(settingsRes.data);
        setBranding(brandingRes.data);
      } catch (err) {
        console.error('Settings fetch error:', err);
      }
      setLoading(false);
    };
    fetchAll();
  }, [tenantId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/daycare/${tenantId}/settings`, settings);
      setSettings(res.data);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const handleSaveBranding = async () => {
    setSavingBrand(true);
    try {
      const res = await axios.put(`${API}/branding/${tenantId}`, {
        brand_name: branding.brand_name,
        primary_color: branding.primary_color,
        secondary_color: branding.secondary_color,
      });
      setBranding(res.data);
      if (onBrandingChange) onBrandingChange(res.data);
      toast.success('Branding saved');
    } catch {
      toast.error('Failed to save branding');
    }
    setSavingBrand(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('File too large. Max 2MB.'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/branding/${tenantId}/logo`, formData);
      setBranding(b => ({ ...b, logo_url: res.data.logo_url }));
      if (onBrandingChange) onBrandingChange({ ...branding, logo_url: res.data.logo_url });
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  if (loading || !settings) {
    return <div className="dc-dash-loading">Loading settings...</div>;
  }

  const logoSrc = branding?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : '';

  return (
    <div className="dc-settings" data-testid="daycare-settings">
      <div className="dc-settings-header">
        <h2>Daycare Settings</h2>
      </div>

      <div className="dc-settings-grid">
        {/* Branding */}
        <div className="dc-settings-card dc-settings-card-wide" data-testid="branding-section">
          <div className="dc-settings-card-head">
            <h3><Palette size={18} /> Branding</h3>
            <button className="dc-save-btn" onClick={handleSaveBranding} disabled={savingBrand} data-testid="dc-save-branding">
              <Save size={16} /> {savingBrand ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
          <div className="dc-brand-grid">
            {/* Logo */}
            <div className="dc-brand-logo-area">
              <label>Logo</label>
              <div className="dc-logo-preview" data-testid="logo-preview" onClick={() => logoInputRef.current?.click()}>
                {logoSrc ? (
                  <img src={logoSrc} alt="Logo" />
                ) : (
                  <div className="dc-logo-placeholder"><Image size={32} /><span>Click to upload</span></div>
                )}
              </div>
              <input ref={logoInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={handleLogoUpload} style={{ display: 'none' }} />
              <button className="dc-upload-btn" onClick={() => logoInputRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
                <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload Logo'}
              </button>
              <span className="dc-form-hint">PNG, JPG, SVG. Max 2MB.</span>
            </div>
            {/* Fields */}
            <div className="dc-brand-fields">
              <div className="dc-form-field">
                <label>Brand Name</label>
                <input type="text" value={branding?.brand_name || ''} onChange={e => setBranding(b => ({ ...b, brand_name: e.target.value }))} placeholder="Your Brand Name" data-testid="brand-name-input" />
              </div>
              <div className="dc-form-row">
                <div className="dc-form-field">
                  <label>Primary Color</label>
                  <div className="dc-color-input">
                    <input type="color" value={branding?.primary_color || '#FACC15'} onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))} data-testid="primary-color-input" />
                    <input type="text" value={branding?.primary_color || '#FACC15'} onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))} placeholder="#FACC15" />
                  </div>
                </div>
                <div className="dc-form-field">
                  <label>Secondary Color</label>
                  <div className="dc-color-input">
                    <input type="color" value={branding?.secondary_color || '#1A2139'} onChange={e => setBranding(b => ({ ...b, secondary_color: e.target.value }))} data-testid="secondary-color-input" />
                    <input type="text" value={branding?.secondary_color || '#1A2139'} onChange={e => setBranding(b => ({ ...b, secondary_color: e.target.value }))} placeholder="#1A2139" />
                  </div>
                </div>
              </div>
              {/* Preview */}
              <div className="dc-brand-preview" style={{ background: branding?.secondary_color || '#1A2139' }}>
                <span style={{ color: branding?.primary_color || '#FACC15', fontWeight: 700, fontSize: 16 }}>{branding?.brand_name || 'Your Brand'}</span>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <span style={{ padding: '4px 12px', background: branding?.primary_color || '#FACC15', color: branding?.secondary_color || '#1A2139', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>Button</span>
                  <span style={{ padding: '4px 12px', border: `1px solid ${branding?.primary_color || '#FACC15'}`, color: branding?.primary_color || '#FACC15', borderRadius: 6, fontSize: 12 }}>Outline</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* General */}
        <div className="dc-settings-card">
          <div className="dc-settings-card-head">
            <h3><Home size={18} /> General</h3>
            <button className="dc-save-btn" onClick={handleSave} disabled={saving} data-testid="dc-save-settings">
              <Save size={16} /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
          <div className="dc-form-field">
            <label>Daycare Name</label>
            <input type="text" value={settings.daycare_name || ''} onChange={e => setSettings(s => ({ ...s, daycare_name: e.target.value }))} placeholder="Your Daycare Name" />
          </div>
          <div className="dc-form-field">
            <label>Classrooms (comma-separated)</label>
            <input type="text" value={(settings.classrooms || []).join(', ')} onChange={e => setSettings(s => ({ ...s, classrooms: e.target.value.split(',').map(c => c.trim()).filter(Boolean) }))} placeholder="Infants, Toddlers, Preschool, Pre-K" />
          </div>
        </div>

        {/* Hours */}
        <div className="dc-settings-card">
          <h3><Clock size={18} /> Operating Hours</h3>
          <div className="dc-form-row">
            <div className="dc-form-field">
              <label>Open</label>
              <input type="time" value={settings.hours?.open || '07:00'} onChange={e => setSettings(s => ({ ...s, hours: { ...s.hours, open: e.target.value } }))} />
            </div>
            <div className="dc-form-field">
              <label>Close</label>
              <input type="time" value={settings.hours?.close || '18:00'} onChange={e => setSettings(s => ({ ...s, hours: { ...s.hours, close: e.target.value } }))} />
            </div>
          </div>
        </div>

        {/* Tuition */}
        <div className="dc-settings-card">
          <h3><DollarSign size={18} /> Tuition Settings</h3>
          <div className="dc-form-field">
            <label>Monthly Rate ($)</label>
            <input type="number" value={settings.tuition_settings?.monthly_rate || 0} onChange={e => setSettings(s => ({ ...s, tuition_settings: { ...s.tuition_settings, monthly_rate: Number(e.target.value) } }))} />
          </div>
          <div className="dc-form-row">
            <div className="dc-form-field">
              <label>Late Fee ($)</label>
              <input type="number" value={settings.tuition_settings?.late_fee || 0} onChange={e => setSettings(s => ({ ...s, tuition_settings: { ...s.tuition_settings, late_fee: Number(e.target.value) } }))} />
            </div>
            <div className="dc-form-field">
              <label>Due Day of Month</label>
              <input type="number" min="1" max="28" value={settings.tuition_settings?.due_day || 1} onChange={e => setSettings(s => ({ ...s, tuition_settings: { ...s.tuition_settings, due_day: Number(e.target.value) } }))} />
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="dc-settings-card">
          <h3><Bell size={18} /> Notification Preferences</h3>
          <label className="dc-checkbox">
            <input type="checkbox" checked={settings.notification_preferences?.sms_enabled ?? true} onChange={e => setSettings(s => ({ ...s, notification_preferences: { ...s.notification_preferences, sms_enabled: e.target.checked } }))} />
            <span>Enable SMS Notifications</span>
          </label>
          <label className="dc-checkbox">
            <input type="checkbox" checked={settings.notification_preferences?.email_enabled ?? true} onChange={e => setSettings(s => ({ ...s, notification_preferences: { ...s.notification_preferences, email_enabled: e.target.checked } }))} />
            <span>Enable Email Notifications</span>
          </label>
          <label className="dc-checkbox">
            <input type="checkbox" checked={settings.notification_preferences?.auto_report ?? true} onChange={e => setSettings(s => ({ ...s, notification_preferences: { ...s.notification_preferences, auto_report: e.target.checked } }))} />
            <span>Auto-send Daily Reports to Parents</span>
          </label>
        </div>
      </div>
    </div>
  );
}
